/* Mission Control: orbital sunrise.
   A single-pass WebGL scene: starfield, Earth's night-side limb, layered atmosphere,
   and a sun that crests the horizon on every load. Scroll lifts the camera.
   Canvas: <canvas data-mc-orbit="hero|close">. Falls back to the CSS ground if WebGL is unavailable. */
(() => {
  const FRAG = `
precision highp float;
uniform vec2 uRes;
uniform float uTime;
uniform float uIntro;
uniform float uScroll;
uniform float uLimb;
uniform float uSunX;
uniform float uSunLift;
uniform float uStars;
uniform float uRetro;
uniform float uMode; // 0 sunrise, 1 eclipse, 2 aurora, 3 moonrise
uniform vec3 uWarm;  // the sun's warm limb light (time of day)
uniform vec3 uAir;   // the atmosphere's colour (time of day)
uniform vec3 uDust;  // galactic dust tint (time of day)
uniform vec3 uLamp;  // the lantern: xy in frame units, z strength (0 off)

float hash(vec2 p){ p = fract(p*vec2(123.34, 456.21)); p += dot(p, p+45.32); return fract(p.x*p.y); }
float noise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  float a = hash(i), b = hash(i+vec2(1.,0.)), c = hash(i+vec2(0.,1.)), d = hash(i+vec2(1.,1.));
  vec2 u = f*f*(3.-2.*f);
  return mix(mix(a,b,u.x), mix(c,d,u.x), u.y);
}
float fbm(vec2 p){
  float v = 0., a = .5;
  for (int i = 0; i < 5; i++){ v += a*noise(p); p = p*2.03 + vec2(1.7, 9.2); a *= .5; }
  return v;
}
float starLayer(vec2 uv, float scale, float seed, float twinkle){
  vec2 g = uv*scale;
  vec2 id = floor(g), f = fract(g) - .5;
  float h = hash(id + seed);
  if (h < .82) return 0.;
  vec2 off = vec2(hash(id+seed+3.1), hash(id+seed+7.7)) - .5;
  float d = length(f - off*.7);
  float size = mix(.018, .06, pow(hash(id+seed+1.3), 6.));
  float tw = mix(1., .55 + .45*sin(uTime*(1.2 + h*2.5) + h*40.), twinkle);
  return smoothstep(size, 0., d) * (h - .82) * 5.5 * tw;
}

void main(){
  vec2 uv = (gl_FragCoord.xy - .5*uRes) / uRes.y;
  float mSun = step(uMode, .5), mEcl = step(.5, uMode)*step(uMode, 1.5), mAur = step(1.5, uMode)*step(uMode, 2.5), mMoon = step(2.5, uMode);
  float aspect = uRes.x / uRes.y;

  // Intro phases
  float starsIn = smoothstep(0., .25, uIntro);
  float ignite  = smoothstep(.12, .72, uIntro);
  float crest   = smoothstep(.45, 1., uIntro);

  // Planet geometry: huge sphere, limb near the bottom of the frame
  float R = max(aspect, 1.) * 1.55 + .35;
  // The planet falls away as the camera climbs: quick enough that the limb crosses the rising results briefly
  float limbY = -.5 + uLimb - (uScroll*.35 + uScroll*uScroll*.5);
  vec2 c = vec2(0., limbY - R);
  vec2 pc = uv - c;
  float r = length(pc);
  float d = r - R;

  // Sun sits on the limb at uSunX, rising through the intro and the scroll
  vec2 sdir = normalize(vec2(uSunX, R));
  float lift = mix(-.035, .006, crest) + uSunLift + uScroll*.05;
  vec2 sun = c + sdir*(R + lift);
  vec2 ts = uv - sun;
  float sd = length(ts);

  // Along-limb distance from the sun (for the ignition sweep)
  float ang = abs(atan(pc.x, pc.y) - atan(sdir.x, sdir.y));
  float along = ang * R;
  float spread = mix(.0, 3.2, ignite);
  float lit = smoothstep(spread, spread*.25, along);
  float nearSun = exp(-along*1.9);

  vec3 col = vec3(0.);

  // Deep field: faint galactic dust, cooler away from the sun
  vec2 q = uv*1.3 + vec2(uTime*.004, 0.) + vec2(0., uScroll*.12);
  float dust = fbm(q*1.6 + fbm(q*2.3));
  float band = exp(-pow((uv.y*.8 - uv.x*.35 - .18)*2.4, 2.));
  col += uDust * pow(dust, 3.) * band * .55 * starsIn;
  col += uDust * vec3(.3, .35, .35) * dust * .35 * starsIn;

  // Stars, three parallax depths
  vec2 su = uv + vec2(uTime*.0015, uScroll*.06);
  float s = starLayer(su, 38., 1., 1.) + starLayer(su*1.0 + vec2(0., uScroll*.05), 74., 7., .6) * .7 + starLayer(su + vec2(0., uScroll*.1), 140., 13., .3) * .5;
  float glare = 1. - smoothstep(.5, 0., sd) * crest;
  col += vec3(.85, .9, 1.) * s * uStars * starsIn * glare;

  // Planet body and atmosphere, blended across one pixel so the limb never stair-steps
  float px = 1.5 / uRes.y;
  vec3 planet = vec3(0.);
  {
    float rr = min(r, R);
    float z = sqrt(max(R*R - rr*rr, 0.)) / R;
    vec3 n = normalize(vec3(pc / R, z));
    vec3 L = normalize(vec3(sdir*.5, -1.));
    float diff = max(dot(n, L), 0.);
    vec2 sp = pc / R * 6. + vec2(uTime*.006, 0.);
    float cloud = smoothstep(.45, .85, fbm(sp*3.2 + fbm(sp*2.)));
    float ocean = fbm(sp*1.4);
    vec3 dayCol = mix(vec3(.02, .07, .16), vec3(.05, .16, .30), ocean);
    dayCol = mix(dayCol, vec3(.72, .80, .90), cloud*.7);
    float term = pow(diff, 1.2) * lit * (1. - .85*mEcl - .9*mAur - .75*mMoon);
    vec3 night = vec3(.006, .010, .022) + vec3(.01, .02, .05) * cloud;
    float city = smoothstep(.93, 1., noise(sp*55.)) * smoothstep(.35, .6, ocean) * (1. - term) * .3;
    planet = night + dayCol * term * 2.2 + vec3(1., .72, .38) * city * starsIn;
    planet += uAir * vec3(.76, .81, 1.) * exp(min(d, 0.)*140.) * .55 * (.35 + .65*lit) * ignite;
  }
  vec3 air = col;
  {
    float dd = max(d, 0.);
    float thin = exp(-dd*260.);
    float mid = exp(-dd*55.);
    float wide = exp(-dd*9.);
    vec3 warm = uWarm;
    vec3 blue = uAir;
    vec3 atm = warm * thin * nearSun * 1.4 * (mSun + mEcl*1.2) + blue * (mid*.9 + thin*.7) + uAir * vec3(.55, .56, .9) * wide * .22;
    air += atm * (.18 + .82*lit) * ignite * (.55 + .8*nearSun) * (1. - .45*mAur - .35*mMoon);
    // Aurora: slow curtains of green fading to violet above the night-side limb
    {
      float xs = atan(pc.x, pc.y) * R;
      float cur = noise(vec2(xs*16. + uTime*.12, 1.3)) * noise(vec2(xs*4.5 - uTime*.05, 7.1));
      float fall = smoothstep(0., .012, dd) * exp(-dd*11.);
      vec3 aurC = mix(vec3(.18, 1., .62), vec3(.52, .4, 1.), smoothstep(.02, .14, dd));
      air += aurC * cur * fall * 1.5 * ignite * mAur;
    }
    // Eclipse: the sun hides behind the planet and its corona rings the limb
    air += vec3(1., .94, .84) * exp(-dd*120.) * (exp(-along*1.1)*1.5 + .45) * crest * mEcl;
    // Retro layer (hero only): a faint 3px scanline shimmer rides the atmosphere line,
    // brightest in a slow band that travels out along the limb from the sun
    float scan = step(1.5, mod(gl_FragCoord.y, 3.));
    float band = exp(-pow(along - mod(uTime*.18, 3.2) + .2, 2.) * 5.);
    float shimmer = uRetro * ignite * exp(-dd*38.) * (.3 + .7*band);
    air *= 1. - shimmer * scan * .38;
    air += uAir * shimmer * (1. - scan) * .05;
    // The lantern: a soft warm presence that reveals fainter stars in the sky (never on the planet)
    if (uLamp.z > 0.) {
      vec2 ld = uv - uLamp.xy;
      float l2 = dot(ld, ld);
      air += vec3(1., .76, .46) * exp(-l2*7.) * .05 * uLamp.z;
      // only pixels inside the light pay for the hidden stars
      if (l2 < .2) {
        float reveal = exp(-l2*16.) * uLamp.z;
        float extra = starLayer(su + vec2(3.7, 1.1), 210., 31., .4) + starLayer(su + vec2(.9, 5.3), 120., 47., .2) * .6;
        air += vec3(.86, .9, 1.) * extra * reveal * .8 * starsIn * glare;
      }
    }
  }
  col = mix(planet, air, smoothstep(-px, px, d));

  // Sun: core, bloom, limb-tangent streak, six-point burst
  {
    float occl = smoothstep(-.01, .004, d);
    float core = exp(-sd*sd*9000.);
    float bloom = .012 / (sd + .02);
    vec2 tang = vec2(sdir.y, -sdir.x);
    float streak = exp(-abs(dot(ts, sdir))*260.) * exp(-abs(dot(ts, tang))*6.5);
    float a = atan(ts.y, ts.x);
    float burst = pow(abs(cos(a*3.)), 80.) * exp(-sd*15.) * .45;
    float sunI = (core*3. + bloom*.8 + streak*1.1 + burst) * crest * occl;
    col += vec3(1., .93, .8) * sunI * mSun;
    // Eclipse: only the bloom shows past the limb, plus a diamond-ring bead where the sun peeks
    vec2 bp = uv - (c + sdir*R);
    float bead = exp(-dot(bp, bp)*9000.) * occl;
    float rays = pow(noise(vec2(atan(ts.y, ts.x)*11., 2.)), 2.) * exp(-sd*5.5) * smoothstep(0., .02, d);
    col += vec3(1., .95, .88) * (bloom*.9*occl + bead*3. + streak*.5*occl) * crest * mEcl;
    col += vec3(.86, .9, 1.) * rays * .55 * crest * mEcl;
    // Moonrise: a cool cratered disc with a soft halo
    float rm = .036;
    float disc = smoothstep(rm, rm - px*1.5, sd) * smoothstep(-.004, .004, d);
    float crater = .72 + .28*fbm(ts*60. + 3.);
    col = mix(col, vec3(.8, .84, .92)*crater, disc*crest*mMoon);
    col += vec3(.5, .68, 1.) * (.007/(sd + .025)) * crest * mMoon * occl;
  }
  // Glare wash across the frame, gentle so the copy stays legible
  col += uWarm * vec3(.9, 1.27, 2.05) * exp(-sd*4.) * .09 * crest * mSun;

  // Scroll fade: gentle, so the planet reads as falling away rather than switching off
  col *= 1. - uScroll*.28;

  // Tone map and dither
  col = 1. - exp(-col*1.25);
  col = pow(col, vec3(.95));
  col += (hash(gl_FragCoord.xy + fract(uTime)) - .5) / 255.;
  gl_FragColor = vec4(col, 1.);
}`;

  const VERT = `attribute vec2 p; void main(){ gl_Position = vec4(p, 0., 1.); }`;
  // The standard light (time of day off): the original constants of the shader
  const WARM = [1, 0.55, 0.22], AIR = [0.33, 0.68, 1], DUST = [0.1, 0.13, 0.26];

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  function compile(gl, type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  }

  function Orbit(canvas) {
    const variant = canvas.dataset.mcOrbit || 'hero';
    const host = canvas.parentElement;
    const gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'high-performance', preserveDrawingBuffer: false });
    if (!gl) { host.classList.add('mc-orbit--fallback'); return; }

    let prog;
    try {
      prog = gl.createProgram();
      gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
      gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('link');
    } catch (e) {
      host.classList.add('mc-orbit--fallback');
      return;
    }
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const U = {};
    ['uRes', 'uTime', 'uIntro', 'uScroll', 'uLimb', 'uSunX', 'uSunLift', 'uStars', 'uRetro', 'uMode', 'uWarm', 'uAir', 'uDust', 'uLamp'].forEach((n) => (U[n] = gl.getUniformLocation(prog, n)));

    let scale = 1;
    let w = 0, h = 0;
    let visible = true;
    let raf = 0;
    let start = 0;
    let last = 0;
    let slowFrames = 0;
    const INTRO_MS = variant === 'hero' ? 3400 : 2600;
    let introDone = reduced.matches;
    let armed = variant === 'hero';

    // Hero: the sun rises at the centre of the satellite orbit ([data-mc-sun]); solve the planet so it sits there
    const scene = canvas.closest('[data-mc-hero]');
    const anchor = variant === 'hero' && scene ? scene.querySelector('[data-mc-sun]') : null;
    const pinned = window.matchMedia('(min-width: 990px)');
    let target = null;
    function locate() {
      target = null;
      if (!anchor) return;
      const c = canvas.getBoundingClientRect();
      const a = anchor.getBoundingClientRect();
      if (!c.height || !a.width) return;
      target = {
        x: (a.left + a.width / 2 - c.left - c.width / 2) / c.height,
        y: (c.height / 2 - (a.top + a.height / 2 - c.top)) / c.height,
      };
    }

    function size() {
      const rect = canvas.getBoundingClientRect();
      const narrow = rect.width < 720;
      // 16-bit mode (a secret, Theme settings, Effects): the sky renders at a few pixels per pixel, scaled up crisp
      const dpr = document.documentElement.hasAttribute('data-bit16') ? 1 / (narrow ? 3 : 4) : Math.min(window.devicePixelRatio || 1, narrow ? 1.25 : 1.5) * scale;
      w = Math.max(1, Math.round(rect.width * dpr));
      h = Math.max(1, Math.round(rect.height * dpr));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      gl.viewport(0, 0, w, h);
      locate();
      remember();
      return narrow;
    }

    function params() {
      const narrow = w / h < 0.9;
      if (variant === 'close') return { limb: narrow ? 0.1 : 0.13, sunX: 0.0, lift: 0.012, stars: 0.8, mode: 0 };
      if (variant === 'eclipse') return { limb: narrow ? 0.12 : 0.15, sunX: 0.0, lift: -0.05, stars: 0.9, mode: 1 };
      if (variant === 'aurora') return { limb: narrow ? 0.1 : 0.12, sunX: 0.0, lift: -0.4, stars: 1.0, mode: 2 };
      if (variant === 'moon') return { limb: narrow ? 0.09 : 0.12, sunX: narrow ? 0.12 : 0.32, lift: 0.03, stars: 1.0, mode: 3 };
      if (variant === 'page') return { limb: narrow ? 0.03 : 0.05, sunX: narrow ? 0.18 : 0.5, lift: 0.01, stars: 0.8 };
      if (target) {
        // Inverse of the shader's geometry: place the sun (at its crested lift) exactly on the target
        const R = Math.max(w / h, 1) * 1.55 + 0.35;
        const lift = 0.006;
        const k = Math.max(-0.95, Math.min(0.95, target.x / (R + lift)));
        const sunX = (k * R) / Math.sqrt(1 - k * k);
        const limbY = target.y + R - Math.sqrt(1 - k * k) * (R + lift);
        return { limb: limbY + 0.5, sunX, lift: 0.0, stars: 1.0 };
      }
      return { limb: narrow ? 0.42 : 0.33, sunX: narrow ? 0.26 : 0.62, lift: 0.0, stars: 1.0 };
    }

    // Where the canvas sits on screen, for the lantern. Pinned (desktop hero): read live, after scrollProgress has
    // already brought layout up to date this frame. Otherwise from a cached page position, so no layout is forced.
    let docTop = 0, cssLeft = 0, cssW = 0, cssH = 0;
    const remember = () => { const r = canvas.getBoundingClientRect(); docTop = r.top + window.scrollY; cssLeft = r.left; cssW = r.width; cssH = r.height; };
    function rect() {
      if (variant === 'hero' && pinned.matches) return canvas.getBoundingClientRect();
      const top = docTop - window.scrollY;
      return { left: cssLeft, top, width: cssW, height: cssH || 1 };
    }

    // The hero is a pinned scene on desktop: progress runs across the section's extra scroll length
    function scrollProgress() {
      if (variant !== 'hero' || !scene || !pinned.matches) return 0;
      const rect = scene.getBoundingClientRect();
      return Math.min(1, Math.max(0, -rect.top / Math.max(1, rect.height - window.innerHeight)));
    }

    function draw(now) {
      if (!start) start = now;
      const t = now - start;
      const intro = reduced.matches || !armed ? 1 : easeOut(Math.min(1, t / INTRO_MS));
      if (intro >= 1) introDone = true;
      const p = params();
      gl.uniform2f(U.uRes, w, h);
      gl.uniform1f(U.uTime, reduced.matches ? 12.0 : now / 1000);
      gl.uniform1f(U.uIntro, intro);
      gl.uniform1f(U.uScroll, scrollProgress());
      gl.uniform1f(U.uLimb, p.limb);
      gl.uniform1f(U.uSunX, p.sunX);
      gl.uniform1f(U.uSunLift, p.lift);
      gl.uniform1f(U.uStars, p.stars);
      gl.uniform1f(U.uRetro, variant === 'hero' ? 1 : 0);
      gl.uniform1f(U.uMode, p.mode || 0);
      // Time of day (set in the page head) and the lantern (mc-stars.js); without them, the standard light
      const sky = window.McSky;
      gl.uniform3fv(U.uWarm, sky ? sky.warm : WARM);
      gl.uniform3fv(U.uAir, sky ? sky.air : AIR);
      gl.uniform3fv(U.uDust, sky ? sky.dust : DUST);
      const L = window.McLantern;
      if (L && L.s > 0.001) {
        const r = rect();
        gl.uniform3f(U.uLamp, (L.x - r.left - r.width / 2) / r.height, (r.top + r.height / 2 - L.y) / r.height, L.s);
      } else gl.uniform3f(U.uLamp, 0, 0, 0);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    function loop(now) {
      raf = 0;
      if (!visible || document.hidden) return;
      // After the intro, a living background at ~30fps is enough and saves the phone's battery
      if (introDone && now - last < 32) { raf = requestAnimationFrame(loop); return; }
      const dt = now - last;
      last = now;
      draw(now);
      if (dt > 40 && dt < 200 && scale > 0.6) {
        if (++slowFrames > 20) { scale = Math.max(0.6, scale * 0.8); size(); slowFrames = 0; }
      }
      if (!reduced.matches) raf = requestAnimationFrame(loop);
    }

    function kick() { if (!raf && visible) raf = requestAnimationFrame(loop); }

    size();
    host.classList.add('mc-orbit--live');
    if (variant !== 'hero') armed = false;

    const onResize = () => { size(); if (reduced.matches) draw(performance.now()); else kick(); };
    const ro = new ResizeObserver(onResize);
    ro.observe(canvas);
    if (anchor) { ro.observe(anchor); document.fonts?.ready.then(onResize); }
    new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      if (visible) remember();
      if (visible && variant !== 'hero' && !armed && !reduced.matches) { armed = true; start = 0; introDone = false; }
      kick();
    }, { rootMargin: '80px' }).observe(host);
    document.addEventListener('visibilitychange', kick);
    document.addEventListener('mc:bit16', () => { onResize(); draw(performance.now()); });
    window.addEventListener('scroll', () => { if (reduced.matches && variant === 'hero') draw(performance.now()); }, { passive: true });
    reduced.addEventListener?.('change', kick);
    draw(performance.now());
    kick();
  }

  function init() {
    // The journey through space (assets/mc-journey.js) draws this page's sunrises itself; if it stands down, these take over
    if (document.documentElement.classList.contains('mc-journey')) return;
    document.querySelectorAll('canvas[data-mc-orbit]').forEach((c) => { if (!c.__mc) { c.__mc = 1; Orbit(c); } });
  }
  const idle = (fn) => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 700 }) : setTimeout(fn, 300));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => idle(init)); else idle(init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('mc:journey', (e) => { if (e.detail && e.detail.state === 'off') init(); });
})();
